
import { useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

interface Product {
  id: number;
  name: string;
  image: string;
  pdfLink: string;
  description: string;
}

export default function ProductsPage() {
  const [scrolled, setScrolled] = useState(false);

  useState(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const products: Product[] = [
    {
      id: 1,
      name: 'SSFM-50AC',
      image: 'https://static.readdy.ai/image/1ca30779a8cb2bf8f1b8cb2cb409851a/5a032604e24af37e8b0f09f249e6a73a.jpeg',
      pdfLink: 'https://brominesystemsinc.com/wp-content/uploads/2015/02/340016-ssfm-50ac-lit.pdf',
      description: 'Single Stage Flow Meter - Model 50AC'
    },
    {
      id: 2,
      name: 'SSFM-50BC',
      image: 'https://static.readdy.ai/image/1ca30779a8cb2bf8f1b8cb2cb409851a/ec9aa5c4b38f2d4717a8d7d30cf8a385.jpeg',
      pdfLink: 'https://brominesystemsinc.com/wp-content/uploads/2015/02/340017-ssfm-50bc-lithi.pdf',
      description: 'Single Stage Flow Meter - Model 50BC LitHi'
    },
    {
      id: 3,
      name: 'SSFM-50CC',
      image: 'https://static.readdy.ai/image/1ca30779a8cb2bf8f1b8cb2cb409851a/3e37bf7a56a19d6b402ceb0557b7c1b1.jpeg',
      pdfLink: 'https://brominesystemsinc.com/wp-content/uploads/2015/02/340018-ssfm-50cc-lithi.pdf',
      description: 'Single Stage Flow Meter - Model 50CC LitHI'
    },
    {
      id: 4,
      name: 'SSFM-100 Series',
      image: 'https://static.readdy.ai/image/1ca30779a8cb2bf8f1b8cb2cb409851a/c792466f6e3c5a468eb6c07a49d644c0.jpeg',
      pdfLink: 'https://brominesystemsinc.com/wp-content/uploads/2015/02/all-ssfm-100-literature.pdf',
      description: 'Complete SSFM-100 Series Literature'
    },
    {
      id: 5,
      name: 'SSFM-100M-15/25',
      image: 'https://static.readdy.ai/image/1ca30779a8cb2bf8f1b8cb2cb409851a/0e13caae88e064e8df05c684ad5334c0.jpeg',
      pdfLink: 'https://brominesystemsinc.com/wp-content/uploads/2015/02/340020-ssfm-100m-15-25-nov14.pdf',
      description: 'SSFM-100M Models 15 & 25'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation scrolled={scrolled} />
      
      {/* Hero Section */}
      <div className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/8c9b6b24c353aae0af31d4524e350e85.jpg)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl font-bold mb-4">Products</h1>
          <p className="text-xl max-w-2xl mx-auto">Professional Bromine Delivery Systems and Water Treatment Solutions</p>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product) => (
            <div 
              key={product.id}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 group"
            >
              <div className="aspect-square bg-gray-50 flex items-center justify-center p-8 overflow-hidden">
                <img 
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{product.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{product.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Information */}
        <div className="mt-16 bg-gray-50 rounded-lg p-8 border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product Applications</h2>
          <div className="grid md:grid-cols-2 gap-6 text-gray-700">
            <div>
              <h3 className="font-semibold text-lg mb-2 text-teal-600">Marine Applications</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span>Offshore platform water treatment</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span>Vessel potable water systems</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span>Marine cooling water disinfection</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2 text-teal-600">Industrial Applications</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span>Industrial water treatment facilities</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span>Process water bromination</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span>Cooling tower water systems</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
